import Seo from "@/components/Seo";
import { useMemo, useState } from "react";
import { useProducts } from "@/context/ProductsContext";
import { CATEGORIES } from "@/data/mockData";
import ProductCard from "@/components/ProductCard";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Filter, X } from "lucide-react";

const OCCASIONS = ["All", "Daily", "Festive", "Wedding", "Pooja", "Gifting", "Corporate"];
const GENDERS = ["All", "Women", "Men", "Kids", "Unisex"];
const PURITIES = ["All", "925 Sterling Silver", "999 Fine Silver"];
const SORT = { latest: "Latest", asc: "Price: Low to High", desc: "Price: High to Low", name: "Name" };

export default function Shop() {
  const { products: PRODUCTS } = useProducts();
  const priceMin = PRODUCTS.length ? Math.floor(Math.min(...PRODUCTS.map(p => p.price)) / 100) * 100 : 0;
  const priceMax = PRODUCTS.length ? Math.ceil(Math.max(...PRODUCTS.map(p => p.price)) / 100) * 100 : 100000;

  const [category, setCategory] = useState("all");
  const [occasion, setOccasion] = useState("All");
  const [gender, setGender] = useState("All");
  const [purity, setPurity] = useState("All");
  const [inStock, setInStock] = useState(false);
  const [price, setPrice] = useState([priceMin, priceMax]);
  const [sort, setSort] = useState("latest");
  const [openMobile, setOpenMobile] = useState(false);

  const filtered = useMemo(() => {
    let list = PRODUCTS.filter(p =>
      (category === "all" || p.category === category) &&
      (occasion === "All" || p.occasion === occasion) &&
      (gender === "All" || p.gender === gender) &&
      (purity === "All" || p.purity === purity) &&
      (!inStock || p.in_stock !== false) &&
      p.price >= price[0] && p.price <= price[1]
    );
    if (sort === "asc") list = [...list].sort((a, b) => a.price - b.price);
    else if (sort === "desc") list = [...list].sort((a, b) => b.price - a.price);
    else if (sort === "name") list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [category, occasion, gender, purity, inStock, price, sort, PRODUCTS]);

  const reset = () => {
    setCategory("all"); setOccasion("All"); setGender("All"); setPurity("All");
    setInStock(false); setPrice([priceMin, priceMax]); setSort("latest");
  };

  const Filters = (
    <div className="space-y-8">
      <div>
        <div className="overline mb-3">Category</div>
        <div className="max-h-[280px] overflow-y-auto pr-2 space-y-1.5">
          <button
            data-testid="filter-cat-all"
            onClick={() => setCategory("all")}
            className={`w-full text-left text-sm px-3 py-1.5 rounded-md transition-colors ${category === "all" ? "bg-[#1A1A1A] text-white" : "hover:bg-[#F8F9FA] text-[#2E2E2E]"}`}
          >All categories</button>
          {CATEGORIES.map((c) => (
            <button
              key={c.slug}
              data-testid={`filter-cat-${c.slug}`}
              onClick={() => setCategory(c.slug)}
              className={`w-full text-left text-sm px-3 py-1.5 rounded-md transition-colors ${category === c.slug ? "bg-[#1A1A1A] text-white" : "hover:bg-[#F8F9FA] text-[#2E2E2E]"}`}
            >{c.name}</button>
          ))}
        </div>
      </div>

      <div>
        <div className="overline mb-3">Price · ₹{price[0].toLocaleString("en-IN")} — ₹{price[1].toLocaleString("en-IN")}</div>
        <Slider
          data-testid="filter-price-slider"
          min={priceMin}
          max={priceMax}
          step={100}
          value={price}
          onValueChange={setPrice}
          className="mt-3"
        />
      </div>

      <div>
        <div className="overline mb-3">Occasion</div>
        <div className="flex flex-wrap gap-2">
          {OCCASIONS.map((o) => (
            <button
              key={o}
              data-testid={`filter-occ-${o.toLowerCase()}`}
              onClick={() => setOccasion(o)}
              className={`text-[11px] uppercase tracking-[0.14em] px-3 py-1.5 rounded-full border transition-colors ${occasion === o ? "border-[#1A1A1A] bg-[#1A1A1A] text-white" : "border-[#E5E5E5] hover:border-[#1A1A1A]"}`}
            >{o}</button>
          ))}
        </div>
      </div>

      <div>
        <div className="overline mb-3">Gender</div>
        <div className="flex flex-wrap gap-2">
          {GENDERS.map((g) => (
            <button
              key={g}
              data-testid={`filter-gender-${g.toLowerCase()}`}
              onClick={() => setGender(g)}
              className={`text-[11px] uppercase tracking-[0.14em] px-3 py-1.5 rounded-full border transition-colors ${gender === g ? "border-[#1A1A1A] bg-[#1A1A1A] text-white" : "border-[#E5E5E5] hover:border-[#1A1A1A]"}`}
            >{g}</button>
          ))}
        </div>
      </div>

      <div>
        <div className="overline mb-3">Purity</div>
        <div className="flex flex-wrap gap-2">
          {PURITIES.map((p) => (
            <button
              key={p}
              data-testid={`filter-purity-${p.replace(/\s+/g,'-').toLowerCase()}`}
              onClick={() => setPurity(p)}
              className={`text-[11px] uppercase tracking-[0.14em] px-3 py-1.5 rounded-full border transition-colors ${purity === p ? "border-[#1A1A1A] bg-[#1A1A1A] text-white" : "border-[#E5E5E5] hover:border-[#1A1A1A]"}`}
            >{p}</button>
          ))}
        </div>
      </div>

      <label className="flex items-center gap-3 cursor-pointer">
        <Checkbox data-testid="filter-instock" checked={inStock} onCheckedChange={(v) => setInStock(!!v)} />
        <span className="text-sm text-[#2E2E2E]">In-store availability</span>
      </label>

      <button
        data-testid="filter-reset"
        onClick={reset}
        className="text-xs uppercase tracking-[0.18em] underline underline-offset-4 text-[#2E2E2E]/70 hover:text-[#1A1A1A]"
      >Reset all</button>
    </div>
  );

  return (
    <div data-testid="page-shop" className="pt-32 pb-24">
      <Seo path="/shop" title="Shop Silver Jewellery" description="Shop the full SIRIJA silver library — rings, necklaces, bangles, earrings, mangalsutra, pooja articles, silver coins and more. Filter by occasion, price and purity." />
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="overline">Shop all</div>
            <h1 className="mt-4 font-serif font-light text-5xl lg:text-6xl leading-[1] tracking-tight">The Silver Library</h1>
            <p className="mt-4 text-[#2E2E2E]/70 text-sm">{filtered.length} pieces · updated weekly</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="shop-sort-trigger" className="w-[200px] h-11 rounded-full border-[#E5E5E5] font-sans text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(SORT).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <button
              data-testid="shop-mobile-filter-btn"
              onClick={() => setOpenMobile(true)}
              className="lg:hidden h-11 px-5 rounded-full border border-[#E5E5E5] text-xs uppercase tracking-[0.18em] flex items-center gap-2"
            >
              <Filter className="h-4 w-4" strokeWidth={1.5} /> Filters
            </button>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <aside className="hidden lg:block lg:col-span-3">
            <div className="sticky top-28">{Filters}</div>
          </aside>

          <div className="lg:col-span-9">
            {filtered.length === 0 ? (
              <div data-testid="shop-empty" className="py-24 text-center text-[#2E2E2E]/60">No pieces match these filters yet. Try resetting.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
                {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile filter drawer */}
      {openMobile && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpenMobile(false)} />
          <div className="absolute inset-y-0 right-0 w-[88vw] max-w-sm bg-white p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="font-serif text-2xl">Filters</div>
              <button aria-label="Close filters" data-testid="shop-mobile-filter-close" onClick={() => setOpenMobile(false)} className="p-2 rounded-full hover:bg-[#F8F9FA]"><X className="h-4 w-4" /></button>
            </div>
            {Filters}
          </div>
        </div>
      )}
    </div>
  );
}
